import { API_BASE_URL } from '../config/api';

/**
 * @typedef {{
 *   latitude?: number | null,
 *   longitude?: number | null,
 *   city?: string | null,
 *   address?: string | null,
 *   venueName?: string | null,
 *   country?: string | null,
 * }} ClubEventLocation
 */

/**
 * @typedef {{
 *   id: string,
 *   title: string,
 *   description: string,
 *   startDate: string,
 *   endDate: string,
 *   location: ClubEventLocation | null,
 *   price: number | null,
 *   capacity: number | null,
 *   registeredCount: number,
 *   participantCount: number,
 *   isActive: boolean,
 *   isCompleted: boolean,
 *   onlineMeetingLink: string | null,
 *   allowGuests: boolean,
 *   guestCapacity: number,
 *   isRegistered: boolean,
 *   attendanceStatus: number | null,
 *   queueNumber: number | null,
 *   myGuests: ClubEventGuest[],
 * }} ClubEventDetail
 */

/**
 * @typedef {{
 *   id: string,
 *   firstName: string,
 *   lastName: string,
 *   phoneNumber: string | null,
 *   fullName: string | null,
 * }} ClubEventGuest
 */

/**
 * @typedef {Omit<ClubEventGuest, 'id' | 'fullName'>} ClubEventGuestInput
 */

/** API: 3 = İptal edildi, 4 = Bekleme listesi */
export const CLUB_EVENT_ATTENDANCE_STATUS_CANCELLED = 3;
export const CLUB_EVENT_ATTENDANCE_STATUS_WAITLIST = 4;

/**
 * @param {Pick<ClubEventDetail, 'isRegistered' | 'attendanceStatus'> | null | undefined} detail
 */
export function isActiveClubEventRegistration(detail) {
    if (!detail?.isRegistered) return false;

    if (typeof detail.attendanceStatus === 'number') {
        if (
            detail.attendanceStatus === CLUB_EVENT_ATTENDANCE_STATUS_CANCELLED
        ) {
            return false;
        }
        return detail.attendanceStatus > 0;
    }

    return true;
}

/**
 * @param {Pick<ClubEventDetail, 'isRegistered' | 'attendanceStatus'> | null | undefined} detail
 */
export function isClubEventWaitlisted(detail) {
    if (!isActiveClubEventRegistration(detail)) return false;
    return detail.attendanceStatus === CLUB_EVENT_ATTENDANCE_STATUS_WAITLIST;
}

/**
 * @param {Pick<ClubEventDetail, 'isRegistered' | 'attendanceStatus'> | null | undefined} detail
 */
export function isClubEventConfirmedRegistration(detail) {
    if (!isActiveClubEventRegistration(detail)) return false;
    return !isClubEventWaitlisted(detail);
}

const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * @param {string | undefined | null} id
 */
export function isPersistedClubEventGuestId(id) {
    return UUID_RE.test(String(id || '').trim());
}

/**
 * @param {ClubEventGuestInput} guest
 * @returns {ClubEventGuestInput | null}
 */
export function normalizeClubEventGuestInput(guest) {
    const firstName = String(guest.firstName || '').trim();
    const lastName = String(guest.lastName || '').trim();
    const phoneNumber = String(guest.phoneNumber || '').trim();
    if (!firstName || !lastName) return null;
    return {
        firstName,
        lastName,
        phoneNumber: phoneNumber || '',
    };
}

/**
 * @param {unknown} row
 * @returns {ClubEventGuest | null}
 */
export function mapClubEventGuest(row) {
    if (!row || typeof row !== 'object') return null;
    const r = /** @type {Record<string, unknown>} */ (row);
    const id = String(r.id ?? r.guestId ?? '').trim();
    if (!id) return null;

    let firstName = String(r.firstName ?? '').trim();
    let lastName = String(r.lastName ?? '').trim();
    const fullName = String(r.fullName ?? '').trim();

    if (!firstName && !lastName && fullName) {
        const parts = fullName.split(/\s+/).filter(Boolean);
        firstName = parts[0] ?? '';
        lastName = parts.slice(1).join(' ');
    }

    if (!firstName && !lastName) return null;

    const phoneRaw = String(r.phoneNumber ?? r.phone ?? '').trim();

    return {
        id,
        firstName,
        lastName,
        phoneNumber: phoneRaw || null,
        fullName: fullName || null,
    };
}

/**
 * @param {Response} res
 * @param {string} failLabel
 * @returns {Promise<unknown>}
 */
async function parseDetailResponse(res, failLabel) {
    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error(`${failLabel} (yanıt okunamadı)`);
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `${failLabel} (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : failLabel;
        throw new Error(msg);
    }

    return /** @type {{ data?: unknown }} */ (obj).data ?? null;
}

/**
 * @param {unknown} row
 * @returns {ClubEventLocation | null}
 */
function mapLocation(row) {
    if (!row || typeof row !== 'object') return null;
    const l = /** @type {Record<string, unknown>} */ (row);
    return {
        latitude:
            typeof l.latitude === 'number' ? l.latitude : null,
        longitude:
            typeof l.longitude === 'number' ? l.longitude : null,
        city: l.city != null ? String(l.city) : null,
        address: l.address != null ? String(l.address) : null,
        venueName: l.venueName != null ? String(l.venueName) : null,
        country: l.country != null ? String(l.country) : null,
    };
}

/**
 * @param {unknown} row
 * @returns {ClubEventDetail | null}
 */
export function mapClubEventDetail(row) {
    if (!row || typeof row !== 'object') return null;
    const r = /** @type {Record<string, unknown>} */ (row);
    const id = String(r.id ?? '').trim();
    if (!id) return null;

    const startDate = String(r.startDate ?? '');
    const endDate = String(r.endDate ?? startDate);

    return {
        id,
        title: String(r.title || 'Etkinlik'),
        description:
            r.description != null ? String(r.description).trim() : '',
        startDate,
        endDate,
        location: mapLocation(r.location),
        price: typeof r.price === 'number' ? r.price : null,
        capacity: typeof r.capacity === 'number' ? r.capacity : null,
        registeredCount:
            typeof r.registeredCount === 'number' ? r.registeredCount : 0,
        participantCount:
            typeof r.participantCount === 'number' ? r.participantCount : 0,
        isActive: r.isActive !== false,
        isCompleted: r.isCompleted === true,
        onlineMeetingLink:
            r.onlineMeetingLink != null &&
            String(r.onlineMeetingLink).trim()
                ? String(r.onlineMeetingLink).trim()
                : null,
        allowGuests: r.allowGuests === true,
        guestCapacity:
            typeof r.guestCapacity === 'number' ? r.guestCapacity : 0,
        isRegistered: r.isRegistered === true,
        attendanceStatus:
            typeof r.attendanceStatus === 'number' ? r.attendanceStatus : null,
        queueNumber:
            typeof r.queueNumber === 'number' ? r.queueNumber : null,
        myGuests: Array.isArray(r.myGuests)
            ? r.myGuests
                  .map(mapClubEventGuest)
                  .filter(
                      (/** @type {ClubEventGuest | null} */ g) => g != null,
                  )
            : [],
    };
}

/**
 * `GET /clubevents/ClubEvent/GetById?id=`
 * @param {string} accessToken
 * @param {string} eventId
 * @returns {Promise<ClubEventDetail>}
 */
export async function fetchClubEventById(accessToken, eventId) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const id = String(eventId || '').trim();
    if (!id) {
        throw new Error('Geçersiz etkinlik');
    }

    const params = new URLSearchParams({ id });
    const url = `${API_BASE_URL}/clubevents/ClubEvent/GetById?${params}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
    });

    const data = await parseDetailResponse(res, 'Etkinlik detayı alınamadı');
    const mapped = mapClubEventDetail(data);
    if (!mapped) {
        throw new Error('Etkinlik detayı okunamadı');
    }
    return mapped;
}

/**
 * @param {Response} res
 * @param {string} failLabel
 * @returns {Promise<void>}
 */
async function parseMutationResponse(res, failLabel) {
    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error(`${failLabel} (yanıt okunamadı)`);
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `${failLabel} (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : failLabel;
        throw new Error(msg);
    }
}

/**
 * `POST /clubevents/ClubEvent/RegisterForEvent?eventId=`
 * @param {string} accessToken
 * @param {string} eventId
 */
export async function registerForClubEvent(accessToken, eventId) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const id = String(eventId || '').trim();
    if (!id) {
        throw new Error('Geçersiz etkinlik');
    }

    const params = new URLSearchParams({ eventId: id });
    const url = `${API_BASE_URL}/clubevents/ClubEvent/RegisterForEvent?${params}`;
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
    });

    await parseMutationResponse(res, 'Etkinlik kaydı yapılamadı');
}

/**
 * `POST /clubevents/ClubEvent/CancelAttendance?eventId=`
 * @param {string} accessToken
 * @param {string} eventId
 */
export async function cancelClubEventAttendance(accessToken, eventId) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const id = String(eventId || '').trim();
    if (!id) {
        throw new Error('Geçersiz etkinlik');
    }

    const params = new URLSearchParams({ eventId: id });
    const url = `${API_BASE_URL}/clubevents/ClubEvent/CancelAttendance?${params}`;
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
    });

    await parseMutationResponse(res, 'Etkinlik kaydı iptal edilemedi');
}

/**
 * `POST /clubevents/ClubEvent/AddGuest/Guest`
 * @param {string} accessToken
 * @param {string} eventId
 * @param {ClubEventGuestInput} guest
 */
export async function addClubEventGuest(accessToken, eventId, guest) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const id = String(eventId || '').trim();
    if (!id) {
        throw new Error('Geçersiz etkinlik');
    }
    const normalized = normalizeClubEventGuestInput(guest);
    if (!normalized) {
        throw new Error('Misafir bilgileri eksik');
    }

    const url = `${API_BASE_URL}/clubevents/ClubEvent/AddGuest/Guest`;
    const res = await fetch(url, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
            eventId: id,
            firstName: normalized.firstName,
            lastName: normalized.lastName,
            phoneNumber: normalized.phoneNumber || null,
        }),
    });

    await parseMutationResponse(res, 'Misafir eklenemedi');
}

/**
 * `PUT /clubevents/ClubEvent/UpdateGuest/Guest`
 * @param {string} accessToken
 * @param {string} eventId
 * @param {string} guestId
 * @param {ClubEventGuestInput} guest
 */
export async function updateClubEventGuest(
    accessToken,
    eventId,
    guestId,
    guest,
) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const id = String(eventId || '').trim();
    const gId = String(guestId || '').trim();
    if (!id || !gId) {
        throw new Error('Geçersiz istek');
    }
    const normalized = normalizeClubEventGuestInput(guest);
    if (!normalized) {
        throw new Error('Misafir bilgileri eksik');
    }

    const url = `${API_BASE_URL}/clubevents/ClubEvent/UpdateGuest/Guest`;
    const res = await fetch(url, {
        method: 'PUT',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
            eventId: id,
            guestId: gId,
            firstName: normalized.firstName,
            lastName: normalized.lastName,
            phoneNumber: normalized.phoneNumber || null,
        }),
    });

    await parseMutationResponse(res, 'Misafir güncellenemedi');
}

/**
 * `DELETE /clubevents/ClubEvent/RemoveGuest/Guest`
 * @param {string} accessToken
 * @param {string} eventId
 * @param {string} guestId
 */
export async function removeClubEventGuest(accessToken, eventId, guestId) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const id = String(eventId || '').trim();
    const gId = String(guestId || '').trim();
    if (!id || !gId) {
        throw new Error('Geçersiz istek');
    }

    const url = `${API_BASE_URL}/clubevents/ClubEvent/RemoveGuest/Guest`;
    const res = await fetch(url, {
        method: 'DELETE',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ eventId: id, guestId: gId }),
    });

    await parseMutationResponse(res, 'Misafir kaldırılamadı');
}

/**
 * @param {string} accessToken
 * @param {string} eventId
 * @param {ClubEventGuest[]} originalGuests
 * @param {Array<ClubEventGuestInput & { id?: string }>} updatedGuests
 */
export async function syncClubEventGuests(
    accessToken,
    eventId,
    originalGuests,
    updatedGuests,
) {
    const originalIds = new Set(originalGuests.map((guest) => guest.id));
    const submittedIds = new Set();

    for (const guest of updatedGuests) {
        const normalized = normalizeClubEventGuestInput(guest);
        if (!normalized) continue;

        if (guest.id && isPersistedClubEventGuestId(guest.id)) {
            submittedIds.add(guest.id);
            await updateClubEventGuest(
                accessToken,
                eventId,
                guest.id,
                normalized,
            );
        } else {
            await addClubEventGuest(accessToken, eventId, normalized);
        }
    }

    for (const guestId of originalIds) {
        if (!submittedIds.has(guestId)) {
            await removeClubEventGuest(accessToken, eventId, guestId);
        }
    }
}
